/**
 * The page's head tags through the site's helmet shim (K-4, K-5): title, description, canonical, robots, Open
 * Graph, Twitter and the JSON-LD script `#events-page`. `react-helmet-async` resolves to src/lib/helmetShim.js
 * (craco alias), whose `Helmet` writes on commit; the site's `HelmetProvider` sits at the app root.
 *
 * The postbuild page (scripts/events-route.js) baked the same tags without Helmet's marker so crawlers see
 * them before any script runs; once mounted, this component drops those baked copies (the JSON-LD by its
 * `jid`, the rest by selector) so the runtime tags stand alone. Helmet manages only what it wrote itself.
 */
import { useEffect, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { buildHead, headTags, JSONLD_ID } from "../lib/head";
import { serializeJsonLd } from "../lib/jsonld";

const HELMET_ATTR = "data-rh";
const BAKED = [`script#${JSONLD_ID}`, 'meta[name="description"]', 'link[rel="canonical"]', 'meta[name="robots"]', 'meta[property^="og:"]', 'meta[name^="twitter:"]']
  .map((s) => `${s}:not([${HELMET_ATTR}])`).join(", ");

/** Removes the tags the postbuild baked into <head> (everything Helmet does not own). Idempotent. */
export function removeBakedHead(doc = typeof document !== "undefined" ? document : null) {
  if (!doc || !doc.head) return;
  doc.head.querySelectorAll(BAKED).forEach((el) => el.remove());
}

export function EventsHead({ lead = null, shown = [], now, siteOrigin, head: given = null }) {
  const built = useMemo(() => buildHead({ lead, shown, now, siteOrigin }), [lead, shown, now, siteOrigin]);
  const head = given || built;
  useEffect(() => { removeBakedHead(); }, []);
  return (
    <Helmet>
      <title>{head.title}</title>
      {headTags(head).map(([tag, attrs]) => (tag === "link" ? <link key={attrs.rel} {...attrs} /> : <meta key={attrs.name || attrs.property} {...attrs} />))}
      {head.jsonLd.length > 0 && <script type="application/ld+json" id={JSONLD_ID}>{serializeJsonLd(head.jsonLd)}</script>}
    </Helmet>
  );
}
